import { FC, useEffect, useMemo, useRef, useState } from "react";
import { Modal, Form, Button } from "react-bootstrap";
import { Pricesheet } from "@models/pricesheet.model";
import { PricesheetItem } from "@models/pricesheetitem.model";
import { JourneyPath } from "@models/journeyPath.model";
import { CreatePricesheetPayload } from "@services/finance/pricesheets/commands/cmdCreatePricesheet";
import { UpdatePricesheetPayload } from "@services/finance/pricesheets/commands/cmdUpdatePricesheet";
import { PricesheetService } from "@services/finance/pricesheets/pricesheetService";
import { JourneyService } from "@services/journey/journeyService";
import CurrencyInput from "../pricesheet/CurrencyInput";
import PathsAccordion, { duplicateCellIds } from "../pricesheet/PathsAccordion";
import { LineItemRow } from "../pricesheet/PriceSheetLineItem";
import { JourneyStepOption } from "../pricesheet/JourneyStepSelect";
import JourneyPathNameModal, { takenPopulations } from "./JourneyPathNameModal";
import { CommandGetPopulationSegments } from "@services/organizations/commands/cmdGetPopulationSegments";
import type { PopulationSegment } from "@models/populationSegment.model";
import RemoveJourneyPathModal from "./RemoveJourneyPathModal";

const JOURNEY_ID = 4;

export interface PathDraft {
  id: number; // negative for new (unsaved) groups
  name: string;
  // Population the group's items price. Always set on a live group (MPN-1592).
  populationSegmentId: number | null;
}

export interface ItemChanges {
  newItems: LineItemRow[];
  editedItems: LineItemRow[];
  deletedIds: number[];
}

export interface PathChanges {
  newPaths: PathDraft[];
  editedPaths: PathDraft[];
  deletedPathIds: number[];
}

interface Props {
  show: boolean;
  onClose: () => void;
  onSave: (
    payload: CreatePricesheetPayload | UpdatePricesheetPayload,
    itemChanges: ItemChanges,
    pathChanges: PathChanges,
  ) => void;
  pricesheet: Pricesheet | null;
  isSaving: boolean;
  pricesheetItems?: readonly PricesheetItem[];
  organizationId: number;
  contractId: number;
  journeyId: number | null;
}

interface FormState {
  base_price: string;
  is_active: boolean;
}

interface NameModalState {
  open: boolean;
  mode: "add" | "rename";
  pathId?: number;
  initialName?: string;
  initialSegmentId?: number | null;
}

const EMPTY_FORM: FormState = {
  base_price: "",
  is_active: true,
};

const CLOSED_NAME_MODAL: NameModalState = { open: false, mode: "add" };

function toItemDraft(item: PricesheetItem): LineItemRow {
  return {
    id: item.id,
    name: item.name,
    journey_step_id: item.journey_step_id,
    journey_path_id: item.journey_path_id,
    step_price: item.step_price,
    estimated_roi: item.estimated_roi ?? 0,
    unit_price: item.unit_price,
    sequence: item.sequence ?? 0,
    conversion_rule_text: item.conversion_rule_text ?? "",
    description: item.description ?? "",
    is_mandatory: item.is_mandatory ?? false,
    has_dependency: item.has_dependency ?? false,
    is_supplemental: item.is_supplemental ?? false,
    create_date: item.create_date ?? null,
    journey_step_dimension_id: item.journey_step_dimension_id ?? null,
    journey_step_dimension_name: item.journey_step_dimension_name ?? null,
  };
}

function toPathDraft(p: JourneyPath): PathDraft {
  return { id: p.id, name: p.name, populationSegmentId: p.population_segment_id };
}

function itemsEqual(a: LineItemRow, b: LineItemRow): boolean {
  return (
    a.name === b.name &&
    a.journey_step_id === b.journey_step_id &&
    a.journey_path_id === b.journey_path_id &&
    a.step_price === b.step_price &&
    a.estimated_roi === b.estimated_roi &&
    a.unit_price === b.unit_price &&
    a.sequence === b.sequence &&
    a.conversion_rule_text === b.conversion_rule_text &&
    a.description === b.description &&
    a.is_mandatory === b.is_mandatory &&
    a.has_dependency === b.has_dependency
  );
}

function flattenSteps(nodes: any[], depth = 0): JourneyStepOption[] {
  const result: JourneyStepOption[] = [];
  for (const node of nodes) {
    const hasChildren = !!node.children?.length;
    result.push({ id: node.id, name: node.name, depth, hasChildren });
    if (hasChildren) {
      result.push(...flattenSteps(node.children, depth + 1));
    }
  }
  return result;
}

const PricesheetFormModal: FC<Props> = ({
  show,
  onClose,
  onSave,
  pricesheet,
  isSaving,
  pricesheetItems = [],
  organizationId,
  contractId,
  journeyId,
}) => {
  const isEdit = pricesheet !== null;

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [drafts, setDrafts] = useState<LineItemRow[]>([]);
  const [paths, setPaths] = useState<PathDraft[]>([]);
  const [segments, setSegments] = useState<PopulationSegment[]>([]);
  const [contractGroups, setContractGroups] = useState<JourneyPath[]>([]);
  const [stepOptions, setStepOptions] = useState<JourneyStepOption[]>([]);
  const [nameModal, setNameModal] = useState<NameModalState>(CLOSED_NAME_MODAL);
  const [removeConfirm, setRemoveConfirm] = useState<{ pathId: number | null; name: string; itemCount: number } | null>(null);
  const originalItemById = useRef<Map<number, LineItemRow>>(new Map());
  const originalPathById = useRef<Map<number, PathDraft>>(new Map());
  const nextItemIdRef = useRef(-1);
  const nextPathIdRef = useRef(-1);

  useEffect(() => {
    if (!organizationId) return;
    new CommandGetPopulationSegments()
      .run(organizationId, 500, 0)
      .then(setSegments)
      .catch(() => setSegments([]));
  }, [organizationId]);

  // Every live group on the contract — the population rule is contract-wide,
  // and a sheet being created has no groups of its own to consult yet (MPN-1713).
  useEffect(() => {
    if (!show) return;
    new PricesheetService().listContractPaths(contractId).then(setContractGroups);
  }, [show, contractId]);

  useEffect(() => {
    new JourneyService().getJourneySteps(journeyId ?? JOURNEY_ID).then((tree) => {
      setStepOptions(flattenSteps(tree));
    });
  }, [journeyId]);

  useEffect(() => {
    if (!show) return;
    const seededItems = isEdit
      ? pricesheetItems
          .filter((i) => i.pricesheet_id === pricesheet!.id)
          .sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0))
          .map(toItemDraft)
      : [];
    setDrafts(seededItems);
    originalItemById.current = new Map(seededItems.map((d) => [d.id, d]));
    nextItemIdRef.current = -1;
    nextPathIdRef.current = -1;
    setForm(
      pricesheet
        ? { base_price: String(pricesheet.base_price ?? 0), is_active: pricesheet.is_active }
        : EMPTY_FORM,
    );
    if (isEdit && pricesheet) {
      new PricesheetService()
        .listPaths(contractId, pricesheet.id)
        .then((loaded) => {
          const drafted = loaded.map(toPathDraft);
          setPaths(drafted);
          originalPathById.current = new Map(drafted.map((p) => [p.id, p]));
        })
        .catch((err) => {
          console.error("Failed to load pricing groups:", err);
          setPaths([]);
          originalPathById.current = new Map();
        });
    } else {
      setPaths([]);
      originalPathById.current = new Map();
    }
  }, [pricesheet, show, pricesheetItems, isEdit, contractId]);

  const takenSegments = useMemo(
    () => takenPopulations(contractGroups, paths, pricesheet?.id, nameModal.pathId),
    [contractGroups, paths, pricesheet?.id, nameModal.pathId],
  );

  const duplicateItemIds = useMemo(() => duplicateCellIds(drafts), [drafts]);

  const updateForm = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((p) => ({ ...p, [key]: value }));

  const handleAddItem = (pathId: number | null) => {
    const id = nextItemIdRef.current--;
    setDrafts((prev) => [
      ...prev,
      {
        id,
        name: "",
        journey_step_id: 0,
        journey_path_id: pathId,
        step_price: 0,
        estimated_roi: 0,
        unit_price: 0,
        // One past the highest sequence already in this group. The old
        // zero-based count made the first item in every group sequence 0,
        // which used to mean "never bills" (MPN-1589).
        sequence:
          Math.max(
            0,
            ...prev
              .filter((d) => (d.journey_path_id ?? null) === pathId)
              .map((d) => d.sequence ?? 0),
          ) + 1,
        conversion_rule_text: "",
        description: "",
        is_mandatory: false,
        has_dependency: false,
        is_supplemental: false,
        create_date: null,
        journey_step_dimension_id: null,
        journey_step_dimension_name: null,
      },
    ]);
  };

  const handleChangeItem = (id: number, patch: Partial<LineItemRow>) => {
    setDrafts((prev) => prev.map((d) => (d.id === id ? { ...d, ...patch } : d)));
  };

  const handleDeleteItem = (id: number) => {
    setDrafts((prev) => prev.filter((d) => d.id !== id));
  };

  const handleRequestAddPath = () => {
    setNameModal({ open: true, mode: "add" });
  };

  const handleRequestRenamePath = (pathId: number | null) => {
    if (pathId === null) {
      setNameModal({ open: true, mode: "rename" });
      return;
    }
    const current = paths.find((p) => p.id === pathId);
    setNameModal({
      open: true,
      mode: "rename",
      pathId,
      initialName: current?.name ?? "",
      initialSegmentId: current?.populationSegmentId ?? null,
    });
  };

  const handleSubmitName = (name: string, populationSegmentId: number | null) => {
    if (nameModal.mode === "add") {
      const id = nextPathIdRef.current--;
      setPaths((prev) => [...prev, { id, name, populationSegmentId }]);
    } else if (nameModal.mode === "rename" && nameModal.pathId !== undefined) {
      const targetId = nameModal.pathId;
      setPaths((prev) => prev.map((p) => (p.id === targetId ? { ...p, name, populationSegmentId } : p)));
    } else if (nameModal.mode === "rename" && nameModal.pathId === undefined) {
      const id = nextPathIdRef.current--;
      setPaths((prev) => [...prev, { id, name, populationSegmentId }]);
      setDrafts((prev) => prev.map((d) => (d.journey_path_id ? d : { ...d, journey_path_id: id })));
    }
    setNameModal(CLOSED_NAME_MODAL);
  };

  const removePath = (pathId: number | null) => {
    if (pathId === null) {
      setDrafts((prev) => prev.filter((d) => d.journey_path_id));
      return;
    }
    setPaths((prev) => prev.filter((p) => p.id !== pathId));
    setDrafts((prev) => prev.filter((d) => d.journey_path_id !== pathId));
  };

  const handleDeletePath = (pathId: number | null) => {
    if (pathId === null) {
      const itemCount = drafts.filter((d) => !d.journey_path_id).length;
      if (itemCount === 0) return;
      setRemoveConfirm({ pathId: null, name: "Unassigned", itemCount });
      return;
    }
    const target = paths.find((p) => p.id === pathId);
    if (!target) return;
    const itemCount = drafts.filter((d) => d.journey_path_id === pathId).length;
    if (itemCount === 0) {
      removePath(pathId);
      return;
    }
    setRemoveConfirm({ pathId, name: target.name, itemCount });
  };

  const handleSave = () => {
    const payload = {
      base_price: form.base_price ? parseFloat(form.base_price) : 0,
      is_active: form.is_active,
    };

    const newPaths = paths.filter((p) => p.id < 0);
    const editedPaths = paths.filter((p) => {
      if (p.id < 0) return false;
      const orig = originalPathById.current.get(p.id);
      return !!orig && (orig.name !== p.name || orig.populationSegmentId !== p.populationSegmentId);
    });
    const currentPathIds = new Set(paths.filter((p) => p.id > 0).map((p) => p.id));
    const deletedPathIds = Array.from(originalPathById.current.keys()).filter(
      (id) => !currentPathIds.has(id),
    );

    const newItems = drafts.filter((d) => d.id < 0);
    const editedItems = drafts.filter((d) => {
      if (d.id < 0) return false;
      const orig = originalItemById.current.get(d.id);
      return !!orig && !itemsEqual(d, orig);
    });
    const currentItemIds = new Set(drafts.filter((d) => d.id > 0).map((d) => d.id));
    const deletedIds = Array.from(originalItemById.current.keys()).filter((id) => {
      if (currentItemIds.has(id)) return false;
      // Skip items whose original path is being deleted — the cascade handles them
      const orig = originalItemById.current.get(id);
      if (orig?.journey_path_id && deletedPathIds.includes(orig.journey_path_id)) return false;
      return true;
    });

    onSave(
      payload,
      { newItems, editedItems, deletedIds },
      { newPaths, editedPaths, deletedPathIds },
    );
  };

  return (
    <>
      <style>{`
        .pricesheet-form-modal {
          max-width: min(1440px, 95vw);
        }
      `}</style>
      <Modal
        show={show}
        onHide={onClose}
        size="xl"
        centered
        scrollable
        dialogClassName="pricesheet-form-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title as="h5">{isEdit ? "Edit Pricesheet" : "Add Pricesheet"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <div className="d-flex align-items-center justify-content-between mb-3">
              <Form.Check
                type="switch"
                id="pricesheet-active-switch"
                label="Active"
                checked={form.is_active}
                onChange={(e) => updateForm("is_active", e.target.checked)}
              />
              <div className="d-flex align-items-center gap-2">
                <Form.Label className="fw-semibold mb-0">Base Price</Form.Label>
                <div style={{ width: 160 }}>
                  <CurrencyInput
                    flex={1}
                    defaultValue={parseFloat(form.base_price) || 0}
                    onCommit={(val) => updateForm("base_price", String(val))}
                  />
                </div>
              </div>
            </div>

            <PathsAccordion
              paths={paths.map((p) => ({
                ...p,
                populationSegmentName:
                  segments.find((s) => s.id === p.populationSegmentId)?.name ?? null,
              }))}
              rows={drafts}
              duplicateItemIds={duplicateItemIds}
              stepOptions={stepOptions}
              organizationId={organizationId}
              onAddPath={handleRequestAddPath}
              onRenamePath={handleRequestRenamePath}
              onDeletePath={handleDeletePath}
              onAddItem={handleAddItem}
              onChangeItem={handleChangeItem}
              onDeleteItem={handleDeleteItem}
              />
          </Form>
        </Modal.Body>
        <Modal.Footer>
          {duplicateItemIds.size > 0 && (
            <span className="small fw-medium text-danger me-auto">
              <i className="bi bi-exclamation-triangle me-1" />
              {duplicateItemIds.size} rows price the same step in one group
            </span>
          )}
          <Button variant="light" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={isSaving || duplicateItemIds.size > 0}>
            {isSaving ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" /> Saving...
              </>
            ) : (
              <>
                <i className="bi bi-check-circle me-2" />
                {isEdit ? "Save Changes" : "Add Pricesheet"}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <JourneyPathNameModal
        show={nameModal.open}
        mode={nameModal.mode}
        initialName={nameModal.initialName}
        initialSegmentId={nameModal.initialSegmentId}
        segments={segments}
        takenSegments={takenSegments}
        onClose={() => setNameModal(CLOSED_NAME_MODAL)}
        onSubmit={handleSubmitName}
      />

      <RemoveJourneyPathModal
        show={removeConfirm !== null}
        pathName={removeConfirm?.name ?? ""}
        itemCount={removeConfirm?.itemCount ?? 0}
        onClose={() => setRemoveConfirm(null)}
        onConfirm={() => {
          if (removeConfirm) removePath(removeConfirm.pathId);
          setRemoveConfirm(null);
        }}
      />
    </>
  );
};

export default PricesheetFormModal;
